/* eslint-disable react-refresh/only-export-components */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import brandingService from '../services/brandingService';
import { applyBrandingTheme } from '../utils/applyBrandingTheme';

const DEFAULT_BRANDING = {
  nombre_empresa: 'Nova Asistencia',
  logo_url: null,
  favicon_url: null,
  hero_image_url: null,
  color_primario: '#4B7A8F',
  color_secundario: '#9AA481',
  color_acento: '#CDC6FB',
  moneda: 'MXN',
  simbolo_moneda: '$',
  mensaje_bienvenida: 'Sistema de gestión de asistencia y recursos humanos',
  footer_text: null,
  copyright_text: null,
};

const BrandingContext = createContext(null);

export const useBranding = () => {
  const context = useContext(BrandingContext);
  if (context) return context;

  return {
    branding: DEFAULT_BRANDING,
    brandingSettings: DEFAULT_BRANDING,
    loading: false,
    updateBranding: () => {},
    refreshBranding: async () => {},
    formatCurrency: amount =>
      brandingService.formatCurrency(amount, { code: 'MXN', symbol: '$' }),
    getFooterText: () => DEFAULT_BRANDING.mensaje_bienvenida,
    getCopyrightText: () =>
      `© ${new Date().getFullYear()} ${DEFAULT_BRANDING.nombre_empresa}. Todos los derechos reservados.`,
  };
};

export const BrandingProvider = ({ children }) => {
  const [branding, setBranding] = useState(DEFAULT_BRANDING);
  const [loading, setLoading] = useState(true);

  const apply = useCallback(settings => {
    const next = { ...DEFAULT_BRANDING, ...(settings || {}) };
    setBranding(next);

    applyBrandingTheme({
      primary_color: next.color_primario,
      secondary_color: next.color_secundario,
      accent_color: next.color_acento,
      brand_name: next.nombre_empresa,
      favicon_url: next.favicon_url,
    });
    return next;
  }, []);

  const loadBrandingSettings = useCallback(async () => {
    setLoading(true);
    try {
      const settings = await brandingService.getPublicBrandingSettings();
      apply(settings);
    } catch (error) {
      console.warn('Could not load branding settings:', error?.message);
      apply(DEFAULT_BRANDING);
    } finally {
      setLoading(false);
    }
  }, [apply]);

  useEffect(() => {
    loadBrandingSettings();
  }, [loadBrandingSettings]);

  const updateBranding = useCallback(
    newBranding => {
      apply({ ...branding, ...(newBranding || {}) });
    },
    [apply, branding]
  );

  const value = useMemo(
    () => ({
      branding,
      brandingSettings: branding,
      loading,
      updateBranding,
      refreshBranding: loadBrandingSettings,
      formatCurrency: amount =>
        brandingService.formatCurrency(amount, {
          code: branding.moneda,
          symbol: branding.simbolo_moneda,
        }),
      getFooterText: () =>
        branding.footer_text ||
        branding.mensaje_bienvenida ||
        DEFAULT_BRANDING.mensaje_bienvenida,
      getCopyrightText: () =>
        branding.copyright_text ||
        `© ${new Date().getFullYear()} ${branding.nombre_empresa || 'Nova Asistencia'}. Todos los derechos reservados.`,
    }),
    [branding, loading, loadBrandingSettings, updateBranding]
  );

  return (
    <BrandingContext.Provider value={value}>
      {children}
    </BrandingContext.Provider>
  );
};

export default BrandingProvider;
